// src/components/QuestionCard.jsx - MATCHES CSS FILES
import React, { useEffect, useState } from 'react';
import { useQuizContext } from '../hooks/useQuizContext';

/**
 * QuestionCard
 * - Uses semantic <fieldset> / <legend>
 * - Supports single (radio) and multiple (checkbox) types
 * - Dispatches { type: 'ADD_RESPONSE', payload: { questionId, selectedOptions } }
 * 
 * FEATURES:
 * 1. Properly handles option IDs regardless of format (_id, id, or index)
 * 2. Uses option INDEX instead of ID for more reliable tracking
 * 3. Better visual feedback for selected options with CSS
 */
const QuestionCard = ({ question }) => {
  const { responses, dispatch } = useQuizContext();
  const [selected, setSelected] = useState([]);

  // Helper to get consistent option identifier
  const getOptionId = (opt, index) => {
    return opt._id || opt.id || `opt-${index}`;
  };

  useEffect(() => {
    const existing = responses.find(r => r.questionId === question._id);
    if (existing && existing.selectedOptions) {
      setSelected([...existing.selectedOptions]);
      console.log('[QuestionCard] Loaded existing response:', {
        questionId: question._id,
        selectedOptions: existing.selectedOptions
      });
    } else {
      setSelected([]);
    }
  }, [question._id, responses]);

  const handleChange = (optionId) => {
    let updated;
    
    if (question.type === 'single') {
      // For single select (radio), always replace with new selection
      updated = [optionId];
    } else {
      // For multiple select (checkbox), toggle the option
      if (selected.includes(optionId)) {
        updated = selected.filter(id => id !== optionId);
      } else {
        updated = [...selected, optionId];
      }
    }

    console.log('[QuestionCard] Selection changed:', {
      questionId: question._id,
      questionType: question.type,
      optionId,
      previousSelection: selected,
      newSelection: updated
    });

    setSelected(updated);
    dispatch({
      type: 'ADD_RESPONSE',
      payload: { 
        questionId: question._id, 
        selectedOptions: updated 
      }
    });
  };

  // Debug: Log when component renders
  console.log('[QuestionCard] Rendering:', {
    questionId: question._id,
    questionText: question.text?.substring(0, 50),
    optionsCount: question.options?.length,
    selected: selected,
    type: question.type
  });

  if (!question.options || question.options.length === 0) {
    return (
      <section className="mwc-card mwc-question">
        <p>No options available for this question.</p>
      </section>
    );
  }

  return (
    <section className="mwc-card mwc-question" aria-labelledby={`q-${question._id}`}>
      <fieldset className="mwc-question__fieldset">
        <legend id={`q-${question._id}`} className="mwc-question__title">
          {question.text}
        </legend>

        <ul className="mwc-question__options" role="group" aria-label={question.text}>
          {question.options.map((opt, index) => {
            // Get consistent option ID
            const optionId = getOptionId(opt, index);
            const inputId = `opt-${question._id}-${optionId}`;
            const isChecked = selected.includes(optionId);
            const inputType = question.type === 'single' ? 'radio' : 'checkbox';

            return (
              <li 
                key={optionId} 
                className={`mwc-option ${isChecked ? 'mwc-option--selected' : ''}`}
              >
                <label htmlFor={inputId} className="mwc-option__label">
                  <input
                    id={inputId}
                    name={`q-${question._id}`}
                    className="mwc-option__input"
                    type={inputType}
                    value={optionId}
                    checked={isChecked}
                    onChange={() => handleChange(optionId)}
                  />
                  <span className="mwc-option__content">
                    <span className="mwc-option__text">{opt.text}</span>
                    {opt.hint && <span className="mwc-option__hint">{opt.hint}</span>}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>
    </section>
  );
};

export default QuestionCard;